/**
 * Frontend configuration for timeout limits and other settings
 * These values can be adjusted without modifying the code
 */

// Read environment variables with fallbacks
const VUE_APP_API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const VUE_APP_API_TIMEOUT = process.env.VUE_APP_API_TIMEOUT;
const VUE_APP_ICP_ID = process.env.VUE_APP_ICP_ID;
const VUE_APP_POLICE_ID = process.env.VUE_APP_POLICE_ID;
const VUE_APP_POLICE_LOGO_PATH = process.env.VUE_APP_POLICE_LOGO_PATH;

const config = {
  // Default timeout for all API requests (in milliseconds)
  defaultTimeout: VUE_APP_API_TIMEOUT ? parseInt(VUE_APP_API_TIMEOUT) : 60000, // 60 seconds
  
  // Specific timeouts for different operations
  timeouts: {
    // PDF upload timeout (AI processing can take time)
    uploadResume: 120000, // 120 seconds (2 minutes)
    
    // PDF generation timeouts - increased for AI processing
    generatePdf: 120000, // 120 seconds (2 minutes) - basic PDF generation
    generatePdfWithAiLayout: 300000, // 300 seconds (5 minutes) - AI-enhanced PDF
    
    // AI analysis timeouts
    starAnalysis: 180000, // 180 seconds (3 minutes)
    synthesizeResumeFromSTAR: 180000, // 180 seconds (3 minutes)
    
    // Style transformation timeouts
    transformStyleAsync: 30000, // 30 seconds for job creation
    batchTransformStyleAsync: 60000, // 60 seconds for batch job creation
    getStyleTransformJobResult: 30000, // 30 seconds for getting job result
    
    // Translation timeouts
    translateMarkdown: 120000, // 120 seconds (2 minutes) - synchronous translation
    translateText: 60000, // 60 seconds (1 minute) - simple text translation
    batchTranslateMarkdown: 300000, // 300 seconds (5 minutes) - batch translation
    translateMarkdownAsync: 30000, // 30 seconds for async job creation
    batchTranslateMarkdownAsync: 60000, // 60 seconds for batch async job creation
    getTranslationJobResult: 30000, // 30 seconds for getting translation job result
    getTranslationExamples: 30000, // 30 seconds for getting examples
    
    // Other operations
    generateStarAnalysisPdf: 120000, // 120 seconds (2 minutes)
    
    // Async job polling
    jobPolling: 30000, // 30 seconds for job status checks

    // Job recommendation agent (LLM + tools can take time)
    postAgent: 120000, // 120 seconds (2 minutes)
    postAgentStream: 180000, // 180 seconds for streaming endpoint
    
    // Custom timeout for specific components if needed
    components: {
      // Example: AiSuggestions component timeout
      aiSuggestions: 30000, // 30 seconds
    }
  },
  
  // Retry configuration
  retry: {
    maxRetries: 3,
    retryDelay: 1000, // 1 second initial delay
    maxRetryDelay: 10000, // 10 seconds maximum delay
  },
  
  // API configuration
  api: {
    // Use environment variable if set, otherwise default to '/api' for same-domain deployment
    baseURL: VUE_APP_API_BASE_URL || '/api',
    headers: {
      'Content-Type': 'application/json'
    }
  },
  
  // Feature flags
  features: {
    enableAiLayout: true,
    enableStarAnalysis: true,
    enableRealTimeUpdates: true,
    emailVerificationFix: true,
  },
  
  // UI configuration
  ui: {
    showTimeoutNotifications: true,
    autoRefreshInterval: 30000, // 30 seconds
    maxFileSize: 10485760, // 10MB in bytes
    // Optional announcement strip (DeepSeek-style top banner). Set to null to hide.
    announcement: {
      text: 'New: Job Recommendations — find roles that match your resume.',
      link: '/job-recommendations',
      linkLabel: 'Try it'
    }
  },
  
  // Footer configuration
  footer: {
    icpId: VUE_APP_ICP_ID || "",
    policeId: VUE_APP_POLICE_ID || "",
    policeLogoPath: VUE_APP_POLICE_LOGO_PATH || "/images/police.png",
    showFooter: true
  }
}

// Export configuration
export default config

// Helper function to get timeout for a specific operation
export function getTimeout(operation) {
  if (config.timeouts[operation]) {
    return config.timeouts[operation]
  }
  return config.defaultTimeout
}

// Helper function to update configuration (for development/debugging)
export function updateConfig(newConfig) {
  Object.assign(config, newConfig)
}
